import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { Subscription, tap } from 'rxjs';

import { ByocCourseService } from '../../service/course.service';
import { ByocService } from '../../../service/byoc.service';
import { Item } from '../../../model/byoc.model';
import { MessageService } from '@app/service/message.service';
import { TranslateService } from '@ngx-translate/core';
import { Course } from '@app/core/model/library.model';
import { ByocEpisodePopupService } from '@app/byoc/popup/episode/service/popup.service';
import { DropdownItem } from '@app/shared/forms/dropdown-multi/model/dropdown.model';
import { control } from '../../../utils';

@Component({
    selector: 'app-byoc-course-step-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss'],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- legacy component, migrate to OnPush when touched
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ByocCourseStepGeneralComponent implements OnInit, OnDestroy {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly byocService = inject(ByocService);
    private readonly byocCourseService = inject(ByocCourseService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly byocEpisodePopupService = inject(ByocEpisodePopupService);

    readonly placeholderLevels: DropdownItem = {
        id: '',
        name: this.translateService.instant('v3-placeholder-select'),
        icon: '',
    };

    /**
     * Loading Flag
     */
    loaded = false;

    /**
     * Available colors
     */
    colors: Item[] = [];

    /**
     * Available levels
     */
    levels: Item[] = [];

    saving = false;

    private subscriptions: Subscription = new Subscription();

    ngOnInit() {
        this.subscriptions.add(
            this.byocService.courseOptions().subscribe((options: [Item[], Item[]]) => {
                [this.colors, this.levels] = options;
                this.selectColor(this.colors[Math.floor(Math.random() * this.colors.length)].name);
                this.loaded = true;
                this.changeDetectorRef.detectChanges();
            }),
        );
        this.subscriptions.add(
            this.byocCourseService.form.valueChanges.subscribe(() => {
                this.patchForm();
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Title Control
     */
    get controlTitle() {
        return control(this.byocCourseService.form.get('title'));
    }

    /**
     * Content/Description Control
     */
    get controlContent() {
        return control(this.byocCourseService.form.get('content'));
    }

    /**
     * Notes Control
     */
    get controlNotes() {
        return control(this.byocCourseService.form.get('notes'));
    }

    /**
     * Color Control
     */
    get controlColor() {
        return control(this.byocCourseService.form.get('color'));
    }

    /**
     * Content/Description Control
     */
    get controlLevel() {
        return control(this.byocCourseService.form.get('level'));
    }

    /**
     * Chceck if controls are invalid
     */
    get invalid(): boolean {
        return this.controlTitle.invalid || this.controlContent.invalid || this.controlLevel.invalid || this.controlColor.invalid;
    }

    get isNameTooLong() {
        return this.byocCourseService.form.get('title')?.hasError('maxlength');
    }

    /**
     * Set Color
     */
    selectColor(color: string): void {
        this.byocCourseService.form.patchValue({
            color: color,
        });
    }

    cancelClick(): void {
        this.byocCourseService.close('btn-close');
    }

    nextClick(): void {
        if (this.invalid) {
            return;
        } else {
            if (this.byocCourseService.skipSteps.includes('mission')) {
                this.byocCourseService.step = 'position';
            } else {
                this.byocCourseService.step = 'mission';
            }
            this.changeDetectorRef.markForCheck();
        }
    }

    /**
     * Save Click
     */
    saveClick(): void {
        if (this.invalid) {
            return;
        }
        this.loaded = false;

        // set as publish
        this.byocCourseService.form.patchValue({ status: 2 });
        this.byocCourseService.form.patchValue({ position: 1 });
        this.byocCourseService.form.removeControl('missionId');

        // save and create Episode
        this.subscriptions.add(
            this.byocService
                .createCourse(this.byocCourseService.form.getRawValue())
                .pipe(
                    tap((course: Course) => {
                        this.messageService.setMessage(
                            this.translateService.instant('v3-byoc-popup-create-course-alert-created', { name: course.name }),
                            'info',
                        );
                        this.byocCourseService.close(JSON.stringify(course));
                    }),
                )
                .subscribe(),
        );
    }

    /**
     * saving if skipSteps.includes('position')
     */
    saveNoPositionClick() {
        // set as draft
        this.byocCourseService.form.patchValue({ status: 2 });
        this.byocCourseService.form.patchValue({ position: 1 });

        this.byocCourseService.step = 'warning';
    }

    /**
     * Save and Add Click
     */
    saveAndAddClick(): void {
        if (!this.loaded) {
            return;
        }

        // set as publish
        this.byocCourseService.form.patchValue({ status: 2 });
        this.byocCourseService.form.patchValue({ position: 1 });

        // show loader
        this.saving = true;
        this.changeDetectorRef.detectChanges();

        // save and create Episode
        this.subscriptions.add(
            this.byocService
                .createCourse(this.byocCourseService.form.getRawValue())
                .pipe(
                    tap((course: Course) => {
                        this.messageService.setMessage(
                            this.translateService.instant('v3-byoc-popup-create-course-alert-created', { name: course.name }),
                            'info',
                        );
                        this.byocEpisodePopupService.replacePopup({
                            type: null,
                            courseId: course.id,
                            isFromOutside: true,
                            skipPosition: false,
                            uploadContentType: null,
                            episodeToEditId: null,
                            episodeToEditData: null,
                        });
                    }),
                )
                .subscribe(),
        );
    }

    get isSubmodalOfAddingMission(): boolean {
        return this.byocCourseService.isSubmodal;
    }

    get skipPosition(): boolean {
        return this.byocCourseService.skipSteps.includes('position');
    }

    /**
     * Patch Form
     */
    private patchForm(): void {
        this.byocCourseService.form.patchValue(
            {
                title: this.byocCourseService.form.value.title.split('<').join('').split('>').join(''),
                content: this.byocCourseService.form.value.content.split('<').join('').split('>').join(''),
                notes: this.byocCourseService.form.value.notes.split('<').join('').split('>').join(''),
            },
            { emitEvent: false },
        );
    }
}
